import React, { useCallback, useEffect, useState } from "react";
import Icons from "../icons";
import { useTheme } from "../theme";
import InboxItemPreview from "./InboxItemPreview";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* Inbox — work the AI finished while "auto complete" was off.
   Lives in the header (before the profile menu): a bell-style button with a
   pending badge, a slide-over panel listing each batch, and a Preview dialog
   that shows exactly what the AI built before you Accept (which performs the
   Jira writes) or Decline.

   Personal to the signed-in user: every request carries their userkey, so you
   only ever see (and can only act on) work you asked for. Items created before
   the Inbox became per-user have no owner and stay visible to everyone, marked
   "unassigned". Finished items can be deleted — individually or all at once.

   Open state can be driven from outside (pass `open` + `onOpenChange`) so a
   screen that just parked work can send the user straight here instead of
   telling them to go and find the icon. Left uncontrolled it manages its own.

   `onDecided(inboxkey)` fires whenever an item is accepted, declined or
   removed. The screen that parked that work is still sitting there waiting for
   a decision it will never get, so it needs to hear that the decision happened
   somewhere else. */
export default function InboxPanel({ open: openProp, onOpenChange, onDecided }) {
  const { userkey } = useTheme();
  const controlled = openProp !== undefined;
  const [openSelf, setOpenSelf] = useState(false);
  const open = controlled ? openProp : openSelf;
  const setOpen = useCallback(
    (next) => {
      const value = typeof next === "function" ? next(open) : next;
      if (!controlled) setOpenSelf(value);
      onOpenChange?.(value);
    },
    [controlled, onOpenChange, open],
  );
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [msg, setMsg] = useState("");
  const [preview, setPreview] = useState(null); // the item being previewed
  const [sessions, setSessions] = useState([]); // live AI runs (may be several)
  const [activeRuns, setActiveRuns] = useState(0); // running count (for the bell)
  const [stopping, setStopping] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);

  // Appended to every Inbox/session request so the server scopes to this user.
  const userQ = userkey ? `userkey=${encodeURIComponent(userkey)}` : "";
  const withUser = (url) =>
    userQ ? `${url}${url.includes("?") ? "&" : "?"}${userQ}` : url;

  // Light poll — badge count + active-run indicator. Runs when panel is closed.
  const loadBadge = useCallback(async () => {
    const q = userkey ? `?userkey=${encodeURIComponent(userkey)}` : "";
    try {
      const [iRes, sRes, gRes] = await Promise.all([
        fetch(`${API_BASE_URL}inbox${q}`),
        fetch(`${API_BASE_URL}chat/sessions${q}`).catch(() => null),
        fetch(`${API_BASE_URL}features/sessions${q}`).catch(() => null),
      ]);
      const iBody = await iRes.json().catch(() => ({}));
      setPending(iBody?.data?.pending || 0);
      let active = 0;
      if (sRes && sRes.ok) {
        const sb = await sRes.json().catch(() => ({}));
        active += (Array.isArray(sb?.sessions) ? sb.sessions : []).filter(
          (x) => x.status === "starting" || x.status === "running"
        ).length;
      }
      if (gRes && gRes.ok) {
        const gb = await gRes.json().catch(() => ({}));
        active += (Array.isArray(gb?.data) ? gb.data : []).filter(
          (x) => x.status === "running"
        ).length;
      }
      setActiveRuns(active);
    } catch {
      /* ignore */
    }
  }, [userkey]);

  // Full load — all data. Runs when the panel is open.
  const load = useCallback(async () => {
    setLoading(true);
    const q = userkey ? `?userkey=${encodeURIComponent(userkey)}` : "";
    try {
      const fetches = [
        fetch(`${API_BASE_URL}inbox${q}`),
        fetch(`${API_BASE_URL}chat/sessions${q}`).catch(() => null),
        fetch(`${API_BASE_URL}features/sessions${q}`).catch(() => null),
      ];
      const [res, sres, gres] = await Promise.all(fetches);
      const body = await res.json().catch(() => ({}));
      const data = body?.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setPending(data.pending || 0);

      let allSessions = [];
      if (sres && sres.ok) {
        const sbody = await sres.json().catch(() => ({}));
        allSessions = Array.isArray(sbody?.sessions) ? sbody.sessions : [];
      }
      if (gres && gres.ok) {
        const gbody = await gres.json().catch(() => ({}));
        const genSessions = (Array.isArray(gbody?.data) ? gbody.data : [])
          .filter((s) => s.status === "running")
          .map((s) => ({
            session_id: s.session_id,
            title: s.title || "Artifact generation",
            status: s.status,
            drafts: s.authored_count || 0,
            preconditions: 0,
            containers: 0,
            kind: "playwright",
          }));
        allSessions = [...allSessions, ...genSessions];
      }
      setSessions(allSessions);
      setActiveRuns(
        allSessions.filter(
          (s) => s.status === "starting" || s.status === "running"
        ).length
      );
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [userkey]);

  const running = sessions.filter(
    (s) => s.status === "starting" || s.status === "running"
  );
  const failed = sessions.filter((s) => s.status === "error");

  const stopSession = async (s) => {
    setStopping(s.session_id);
    try {
      await fetch(
        `${API_BASE_URL}chat/sessions/${encodeURIComponent(s.session_id)}/stop`,
        { method: "POST" }
      );
      setMsg("Stopping — it will wrap up at its next checkpoint.");
      await load();
    } catch {
      setMsg("Could not stop that run.");
    } finally {
      setStopping(null);
    }
  };

  // Poll — badge only when closed (60s), full load when open (10s).
  useEffect(() => {
    if (open) {
      load();
      const t = setInterval(load, 10000);
      return () => clearInterval(t);
    }
    loadBadge();
    const t = setInterval(loadBadge, 60000);
    return () => clearInterval(t);
  }, [open, load, loadBadge]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  const act = async (item, what) => {
    setBusyKey(item.inboxkey);
    setMsg("");
    try {
      const res = await fetch(
        withUser(`${API_BASE_URL}inbox/${encodeURIComponent(item.inboxkey)}/${what}`),
        { method: "POST" }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(body?.detail || `HTTP ${res.status}`);
        return;
      }
      const d = body?.data || {};
      if (what === "accept") {
        const errs = (d.errors || []).length;
        const dupes = (d.skipped_duplicates || []).length;
        const dupeNote = dupes
          ? ` · ${dupes} skipped because they already existed`
          : "";
        if (item.kind === "playwright_generation") {
          const saved = d.saved || 0;
          const skipped = d.skipped || 0;
          setMsg(
            `Saved ${saved} test case(s) to the test suite` +
            (skipped ? ` · ${skipped} skipped` : "") +
            dupeNote +
            (errs ? ` · ${errs} error(s)` : "") + "."
          );
        } else {
          const made = (d.created_tests || []).length;
          setMsg(
            `Created ${made} test(s) in Jira${dupeNote}` +
            (errs ? ` · ${errs} error(s)` : "") + "."
          );
        }
      } else {
        setMsg("Declined.");
      }
      setPreview(null);
      load();
      onDecided?.(item.inboxkey);
    } catch (e) {
      setMsg(`Failed: ${e.message}`);
    } finally {
      setBusyKey(null);
    }
  };

  const statusChip = (s) =>
    s === "pending" ? "bg-amber-50 text-amber-700 ring-amber-600/20"
    : s === "accepted" ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
    : "bg-slate-100 text-slate-500 ring-slate-500/20";

  // Remove one finished item. Only clears the Inbox record — whatever was
  // created in Jira or on a board is untouched.
  const remove = async (item) => {
    setBusyKey(item.inboxkey);
    setMsg("");
    try {
      const res = await fetch(
        withUser(`${API_BASE_URL}inbox/${encodeURIComponent(item.inboxkey)}`),
        { method: "DELETE" },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(body?.detail || `HTTP ${res.status}`);
        return;
      }
      setMsg("Removed from your Inbox.");
      if (preview?.inboxkey === item.inboxkey) setPreview(null);
      load();
      onDecided?.(item.inboxkey);
    } catch (e) {
      setMsg(`Failed: ${e.message}`);
    } finally {
      setBusyKey(null);
    }
  };

  const clearCompleted = async () => {
    setConfirmClear(false);
    setMsg("");
    try {
      const res = await fetch(withUser(`${API_BASE_URL}inbox/completed`), {
        method: "DELETE",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(body?.detail || `HTTP ${res.status}`);
        return;
      }
      const n = body?.data?.deleted || 0;
      setMsg(n ? `Cleared ${n} finished item(s).` : "Nothing to clear.");
      load();
    } catch (e) {
      setMsg(`Failed: ${e.message}`);
    }
  };

  const completedCount = items.filter((i) => i.status !== "pending").length;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Inbox — your AI work awaiting approval"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-white/80 transition hover:bg-white/10 hover:text-white"
      >
        <Icons.Inbox className="h-5 w-5" />
        {pending > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white ring-2 ring-brand-900">
            {pending}
          </span>
        )}
        {pending === 0 && activeRuns > 0 && (
          <span
            title={`${activeRuns} AI ${activeRuns === 1 ? "run" : "runs"} in progress`}
            className="absolute -right-0.5 -top-0.5 flex h-3 w-3"
          >
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-indigo-500 ring-2 ring-brand-900" />
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-slate-900/30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
            <div className="flex flex-none items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <Icons.Inbox className="h-4 w-4 text-brand-600" />
                  Inbox
                  {(pending > 0 || running.length > 0) && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                      {pending} pending
                    </span>
                  )}
                </h2>
                <p className="text-xs text-slate-500">
                  Your AI work, waiting for your decision
                </p>
              </div>
              <div className="flex items-center gap-1">
                {completedCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setConfirmClear(true)}
                    title="Delete all finished items from your Inbox"
                    className="rounded-md px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  >
                    Clear finished ({completedCount})
                  </button>
                )}
                <button type="button" onClick={load} title="Refresh"
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                  <Icons.Refresh className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </button>
                <button type="button" onClick={() => setOpen(false)}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                  <Icons.X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {confirmClear && (
              <div className="flex-none border-b border-amber-100 bg-amber-50 px-4 py-2.5">
                <p className="text-xs text-amber-900">
                  Delete {completedCount} finished item
                  {completedCount === 1 ? "" : "s"} from your Inbox? Tests already
                  created in Jira or saved to a test suite are not affected.
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={clearCompleted}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-rose-700"
                  >
                    <Icons.Trash className="h-3.5 w-3.5" />
                    Delete them
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmClear(false)}
                    className="rounded-lg border border-amber-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
                  >
                    Keep them
                  </button>
                </div>
              </div>
            )}

            {msg && (
              <div className="flex-none border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-600">{msg}</div>
            )}

            <div className="cq-scroll min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
              {running.length > 0 && (
                <div className="mb-1 space-y-2">
                  <h4 className="px-1 text-[11px] font-semibold uppercase tracking-wider text-indigo-600">
                    Running now ({running.length})
                  </h4>
                  {running.map((s) => (
                    <div
                      key={s.session_id}
                      className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3"
                    >
                      <div className="flex items-start gap-2">
                        <span className="mt-1 flex-none">
                          <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-75" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-indigo-500" />
                          </span>
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-slate-800">
                            {s.title || "AI session"}
                          </p>
                          <p className="mt-0.5 text-[11px] text-slate-500">
                            {s.status === "starting" ? "Opening the page..." : "Drafting"}
                            {" - "}
                            {s.drafts} test{s.drafts === 1 ? "" : "s"}
                            {s.preconditions > 0 && `, ${s.preconditions} precondition(s)`}
                            {s.containers > 0 && `, ${s.containers} set/plan`}
                            {" so far"}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => stopSession(s)}
                          disabled={stopping === s.session_id}
                          className="flex-none rounded-lg border border-rose-200 bg-white px-2 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                        >
                          {stopping === s.session_id ? "Stopping..." : "End now"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {failed.length > 0 && (
                <div className="mb-1 space-y-2">
                  <h4 className="px-1 text-[11px] font-semibold uppercase tracking-wider text-rose-600">
                    Failed ({failed.length})
                  </h4>
                  {failed.map((s) => (
                    <div
                      key={s.session_id}
                      className="rounded-xl border border-rose-200 bg-rose-50/40 p-3"
                    >
                      <p className="truncate text-sm font-semibold text-slate-800">
                        {s.title || "AI session"}
                      </p>
                      <p className="mt-0.5 break-words text-[11px] text-rose-700">
                        {s.error || "The run stopped unexpectedly."}
                      </p>
                      <p className="mt-1 text-[11px] text-slate-500">
                        Drafted {s.drafts} test(s) before failing.
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {items.length === 0 && running.length === 0 && failed.length === 0 ? (
                <div className="px-2 py-10 text-center">
                  <Icons.Inbox className="mx-auto h-8 w-8 text-slate-300" />
                  <p className="mt-2 text-sm text-slate-400">Nothing here yet.</p>
                  <p className="mt-1 text-xs text-slate-400">
                    This Inbox is yours alone — when a run you started finishes with
                    auto-complete off, its work lands here.
                  </p>
                </div>
              ) : items.map((it) => (
                <div key={it.inboxkey} className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-start gap-2">
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-slate-800">{it.title}</span>
                      {it.summary && <span className="mt-0.5 block text-xs text-slate-500">{it.summary}</span>}
                    </span>
                    <span className={`flex-none rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${statusChip(it.status)}`}>
                      {it.status}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1 text-[10px] text-slate-500">
                    <span className="rounded bg-slate-50 px-1.5 py-0.5 ring-1 ring-slate-200">{it.test_count} test(s)</span>
                    {!it.userkey && (
                      <span
                        title="Created before the Inbox became per-user, so it's visible to everyone."
                        className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-500 ring-1 ring-slate-300"
                      >
                        unassigned
                      </span>
                    )}
                    {it.precondition_count > 0 && (
                      <span className="rounded bg-slate-50 px-1.5 py-0.5 ring-1 ring-slate-200">{it.precondition_count} shared precondition(s)</span>
                    )}
                    {(it.containers || []).map((c) => (
                      <span key={c.kind} className="rounded bg-violet-50 px-1.5 py-0.5 text-violet-700 ring-1 ring-violet-200">
                        + {c.kind === "test_set" ? "Test Set" : "Test Plan"}
                      </span>
                    ))}
                    {(it.link_targets || []).map((t) => (
                      <span key={t} className="rounded bg-slate-50 px-1.5 py-0.5 font-mono ring-1 ring-slate-200">→ {t}</span>
                    ))}
                  </div>
                  {it.status === "accepted" && it.result && (
                    <>
                      <p className="mt-2 text-[11px] text-emerald-700">
                        Created: {(it.result.created_tests || []).join(", ") || "—"}
                      </p>
                      {(it.result.skipped_duplicates || []).length > 0 && (
                        <p className="mt-1 text-[11px] text-amber-700">
                          Skipped as already existing:{" "}
                          {(it.result.skipped_duplicates || [])
                            .map((s) =>
                              s.duplicate_of
                                ? `${s.summary || s.xray_test_key} (→ ${s.duplicate_of})`
                                : s.summary || s.xray_test_key,
                            )
                            .join(", ")}
                        </p>
                      )}
                    </>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" onClick={() => setPreview(it)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
                      <Icons.Eye className="h-3.5 w-3.5" /> Preview
                    </button>
                    {it.status === "pending" && (
                      <>
                        <button type="button" onClick={() => act(it, "accept")} disabled={busyKey === it.inboxkey}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                          {busyKey === it.inboxkey ? <Icons.Spinner className="h-3.5 w-3.5 animate-spin" /> : <Icons.Check className="h-3.5 w-3.5" />}
                          Accept
                        </button>
                        <button type="button" onClick={() => act(it, "decline")} disabled={busyKey === it.inboxkey}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                          <Icons.X className="h-3.5 w-3.5" /> Decline
                        </button>
                      </>
                    )}
                    {it.status !== "pending" && (
                      <button
                        type="button"
                        onClick={() => remove(it)}
                        disabled={busyKey === it.inboxkey}
                        title="Delete this finished item from your Inbox. Tests already created are not affected."
                        className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-2.5 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                      >
                        {busyKey === it.inboxkey ? (
                          <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Icons.Trash className="h-3.5 w-3.5" />
                        )}
                        Remove from Inbox
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Preview — everything the AI built. Shared with Explore & Author so
          approving from either place shows the identical review. */}
      <InboxItemPreview
        item={preview}
        busy={busyKey === preview?.inboxkey}
        onClose={() => setPreview(null)}
        onAccept={() => act(preview, "accept")}
        onDecline={() => act(preview, "decline")}
        onRemove={() => remove(preview)}
      />
    </>
  );
}
